import type { NodeDecisionLog, StepDecision } from './decision-log.js';
import {
  linkSignalsByStepMap,
  linkStepsToTraceActions,
  type LinkerInput,
  type LinkResult,
} from './step-linker.js';

export * from './decision-log.js';
export * from './step-linker.js';
export * from './comparator.js';
export * from './ground-truth.js';
export * from './compose.js';
export * from './run-code-timeline.js';

export interface OptimizeNodeStepsPureInput extends LinkerInput {
  nodeId: string;
}

export interface OptimizeNodeStepsPureResult {
  linkResult: LinkResult;
  decisionLog: NodeDecisionLog;
}

export function optimizeNodeStepsPure(
  input: OptimizeNodeStepsPureInput
): OptimizeNodeStepsPureResult {
  const linkResult = linkStepsToTraceActions(input);
  const decisions: StepDecision[] = [];
  const signalsByStep = linkSignalsByStepMap(linkResult);

  for (const link of linkResult.links) {
    const signals = new Set([
      ...(signalsByStep.get(link.stepOrder) ?? []),
      `action:${link.actionIndex}`,
    ]);
    decisions.push({
      stepOrder: link.stepOrder,
      outcome: 'improved',
      layer: link.layer,
      detail: `linked to trace action ${link.actionIndex}`,
      signals: [...signals],
    });
  }

  for (const carry of linkResult.carryThrough) {
    decisions.push({
      stepOrder: carry.stepOrder,
      outcome: 'carry_through',
      detail: carry.channel,
      signals: [carry.channel],
    });
  }

  for (const pending of linkResult.pendingRowDisambiguation) {
    const signals = new Set([
      ...(signalsByStep.get(pending.stepOrder) ?? []),
      ...pending.candidateActionIndices.map((index) => `candidate:${index}`),
    ]);
    decisions.push({
      stepOrder: pending.stepOrder,
      outcome: 'pending',
      layer: 2,
      detail: 'pending ground-truth row disambiguation',
      candidateActionIndices: pending.candidateActionIndices,
      signals: [...signals],
    });
  }

  for (const gap of linkResult.gaps) {
    const signals = gap.signals ?? signalsByStep.get(gap.stepOrder);
    decisions.push({
      stepOrder: gap.stepOrder,
      outcome: 'gap',
      reason: gap.reason,
      ...(gap.detail ? { detail: gap.detail } : {}),
      ...(signals && signals.length > 0 ? { signals } : {}),
    });
  }

  decisions.sort((a, b) => a.stepOrder - b.stepOrder || outcomeRank(a) - outcomeRank(b));

  const byLayer: Record<string, number> = {};
  const gapsByReason: Record<string, number> = {};
  for (const decision of decisions) {
    if (decision.layer !== undefined && decision.outcome === 'improved') {
      const key = String(decision.layer);
      byLayer[key] = (byLayer[key] ?? 0) + 1;
    }
    if (decision.outcome === 'gap' && decision.reason) {
      gapsByReason[decision.reason] = (gapsByReason[decision.reason] ?? 0) + 1;
    }
  }

  return {
    linkResult,
    decisionLog: {
      nodeId: input.nodeId,
      decisions,
      linkStats: { byLayer, gapsByReason },
    },
  };
}

function outcomeRank(decision: StepDecision): number {
  switch (decision.outcome) {
    case 'improved':
      return 0;
    case 'carry_through':
      return 1;
    case 'incumbent_kept':
      return 2;
    case 'pending':
      return 3;
    case 'gap':
      return 4;
  }
}
